/* eslint-disable css-modules/no-unused-class */
import { Children, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';

// Design System
import { TertiaryButton } from 'jl-design-system/elements/button/Button';
import Form from 'jl-design-system/components/form/Form';
import FormFieldErrorMessage from 'jl-design-system/components/form-field-error-message/FormFieldErrorMessage';
import Bookmark24PxOutlined from 'jl-design-system/elements/icons-jb/Bookmark24PxOutlined';
import { LoadingSpinner } from 'jl-design-system/components/loading-spinner/LoadingSpinner';
import { Stepper } from 'jl-design-system/components/stepper/Stepper';

// Types
import { EditBasketItemProps } from './EditBasketItem.types';
import { ShowItemRemoveOverlayTypes } from '@redux/actions/edit-basket/editBasketActions.types';

// Config
import editBasketItemState, { makeGetShowCollectionHelp } from './EditBasketItem.state';
import { getQuantityText, getSubtotalText } from '@components/order-item/OrderItemHelper';
import getItemQuantityFormConfig, { getFormId, ITEM_QUANTITY_INPUT_ID } from '@utils/form/configs/itemQuantity';
import { shouldRemoveAndSaveForLater } from '@utils/saveForLater/saveForLaterHelp';
import isItemFabric, { itemIsM2M } from '@utils/orderform/isItemFabric';

// Components
import BodyText from '@components/body-text';
import Brand from '@components/brand';
import Container from '@components/container';
import EditBasketItemOverlay from '@components/edit-basket-item-overlay';
import Heading from '@components/heading';
import ProductImageWithMagnifier from '@components/product-image-with-magnifier';

// Styles
import styles from './edit-basket-item.scss';

const EditBasketItem = ({
  children = null,
  disabled = false,
  handleQuantityChange = () => {},
  handleRemoveItem = () => {},
  hideItemRemoveOverlay = () => {},
  isChildItem = false,
  isFirstChild = false,
  isItemUpdating = false,
  isProcessing = false,
  item = undefined,
  lastItemInBasket = false,
  lastItemInList = false,
  onFormSubmit = () => {},
  overlayType = '',
  parentQuantity = 0,
  removeItemApiCallActive = false,
  showItemRemoveOverlay = ({}: ShowItemRemoveOverlayTypes) => {},
  stepperChangeDebounceMsecs = 500,
}: EditBasketItemProps) => {
  if (!item) {
    return null;
  }

  const [pendingQuantityUpdate, setPendingQuantityUpdate] = useState<number | undefined>(undefined);
  const [timeoutHandle, setTimeoutHandle] = useState<NodeJS.Timeout | null>(null);

  const { errorItemId, errorMessage, isExpressCheckout, isSignedIn, editBasketIsRemove } =
    useSelector(editBasketItemState);

  const selectShowCollectionHelp = useMemo(() => makeGetShowCollectionHelp(), []);
  const { showCollectionHelp } = useSelector((state) => selectShowCollectionHelp(state, { isChildItem, item }));

  const handleStepperChange = (newQuantity: number) => {
    const formId = getFormId(id);
    const isCountZero = newQuantity === 0;

    setPendingQuantityUpdate(isCountZero ? 1 : newQuantity);

    if (isCountZero) {
      showItemRemoveOverlay({ itemId: id, isRemove: true });
    } else {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      const newTimeoutHandle = setTimeout(() => {
        handleQuantityChange(formId);
        onFormSubmit({
          itemId: id,
          itemQuantity: newQuantity,
        });
        setPendingQuantityUpdate(undefined);
      }, stepperChangeDebounceMsecs);

      setTimeoutHandle(newTimeoutHandle);
    }
  };

  const handleOnChange = (itemId: string) => (event: any) => {
    const itemQuantity = parseInt(event.target.value, 10);
    const newTimeoutHandle = setTimeout(() => {
      handleFabricQuantityChange(itemQuantity, itemId);
    }, stepperChangeDebounceMsecs);

    setTimeoutHandle(newTimeoutHandle);
  };

  const handleFabricQuantityChange = (itemQuantity: number, itemId: string) => {
    if (itemQuantity === 0) {
      showItemRemoveOverlay({ itemId: id, isRemove: true });
    } else if (itemQuantity > 0) {
      const newTimeoutHandle = setTimeout(() => {
        onFormSubmit({
          itemId,
          itemQuantity,
        });
      }, stepperChangeDebounceMsecs);

      setTimeoutHandle(newTimeoutHandle);
    }
  };

  useEffect(
    () => () => {
      if (timeoutHandle) clearTimeout(timeoutHandle);
    },
    [timeoutHandle],
  );

  const handleShowItemRemoveOverlay = (isRemove = false) => {
    showItemRemoveOverlay({ itemId: id, isRemove });
  };

  const onRemoveItemYes = ({ forceRemove = false }) => {
    handleRemoveItem({ itemId: id, lastItemInBasket, isChildItem, isExpressCheckout, forceRemove });
  };

  const onRemoveItemNo = () => {
    hideItemRemoveOverlay(id);
  };

  const {
    availability = {},
    brand,
    id = '',
    imageUrl,
    name,
    quantity,
    subtotal,
    subtotalDiscounted,
    priceReductionHistory,
    promotionalMessageTitle,
    partnerDiscountRate,
    type = '',
  } = item;

  const hasChildItems = Children.count(children) > 0;
  const renderImage = !!(imageUrl && !isChildItem);
  const showError = errorMessage && id === errorItemId;

  const {
    outlet: isOutlet = false,
    preOrder: isPreOrder = false,
    shipFromStore: isShipFromStore = false,
  } = availability;
  const shouldShowSaveForLater = shouldRemoveAndSaveForLater({
    isChildItem,
    isExpressCheckout,
    isOutlet,
    isShipFromStore,
    isSignedIn,
    isM2M: itemIsM2M(type),
  });

  const showBothRemoveOptions = shouldShowSaveForLater;
  const formId = getFormId(id);
  // TODO: Protect the return function
  const config: any = useMemo(
    () =>
      getItemQuantityFormConfig({
        changeHandler: handleQuantityChange(formId),
        editBasket: true,
        isItemFabric: isItemFabric(item),
        item,
      }),
    [handleQuantityChange, formId, item, isItemFabric],
  );

  const formDisabled = disabled || !!overlayType;
  const removeButtonDisabled = disabled || !!overlayType;
  const headingId = `order-item-name-${id}`;
  const showChildQuantityText = isChildItem && parentQuantity === 1;
  const maxQuantity = isChildItem ? parentQuantity : config.maxValue;

  const SaveForLaterButton = () => (
    <TertiaryButton
      IconComponent={Bookmark24PxOutlined}
      aria-describedby={headingId}
      className={styles.saveForLaterButton}
      data-testid="save-for-later-button"
      disabled={removeButtonDisabled}
      onClick={() => handleShowItemRemoveOverlay(false)}
      small
    >
      Save for later
    </TertiaryButton>
  );

  return (
    <Container
      key={id}
      borderBottom={lastItemInList ? undefined : 'grey-15'}
      paddingY={isChildItem ? undefined : '3'}
      relative
      testId={isChildItem ? 'edit-basket-child-item' : 'edit-basket-item'}
    >
      <Container block={isChildItem} marginTop={isChildItem ? '1' : undefined}>
        {isFirstChild && <p>Additional services</p>}

        <Container block={isChildItem} flex={!isChildItem} marginBottom={isChildItem ? undefined : '1'}>
          {renderImage && <ProductImageWithMagnifier imageUrl={imageUrl} name={name || ''} showMagnifyingIcon />}

          <Container
            background={isChildItem ? 'grey-5' : ''}
            flex={{ flexDirection: 'col', flexItem: '1' }}
            marginTop={isChildItem ? '2' : undefined}
            paddingTop={isChildItem ? '1.5' : undefined}
            paddingX={isChildItem ? '1.5' : undefined}
            testId="edit-basket-item-info"
          >
            <Container flex={{ justifyContent: 'space-between' }}>
              {isChildItem ? (
                <Heading id={headingId} label={name} marginBottom="1" tag="h4" testId="order-item-name" type="xs" />
              ) : (
                <div>
                  <Brand brand={brand} headingTag="h3" />
                  <p data-testid="order-item-name" id={headingId}>
                    {name}
                  </p>
                </div>
              )}

              {(!shouldShowSaveForLater || showBothRemoveOptions) && (
                <TertiaryButton
                  aria-describedby={headingId}
                  aria-label="Remove item"
                  className={cx(styles.removeButton, { [styles.removeChildButton]: isChildItem })}
                  data-testid="remove-item-button"
                  disabled={removeButtonDisabled}
                  onClick={() => handleShowItemRemoveOverlay(true)}
                />
              )}
            </Container>

            {showCollectionHelp && (
              <p>
                <strong>Collection unavailable</strong>
              </p>
            )}

            {isPreOrder && <p>Pre-order</p>}
            {!isChildItem && !isItemUpdating && (
              <>
                {getSubtotalText({
                  brand,
                  sidebar: false,
                  subtotal,
                  subtotalDiscounted,
                  styles,
                  basketView: true,
                  isChildItem,
                  priceReductionHistory,
                  promotionalMessageTitle,
                  partnerDiscountRate,
                  showPromotionalMessageTitle: true,
                  showPartnerDiscountRate: true,
                })}
              </>
            )}
          </Container>
        </Container>
        <Container
          className={cx(styles.itemDetailsContainer, {
            [styles.fabricItem]: isItemFabric(item),
            [styles.itemDetailsChildContainer]: isChildItem,
          })}
          relative
          testId="edit-basket-item-details"
        >
          {isItemFabric(item) && (
            <div>
              <Container flex={shouldShowSaveForLater && { justifyContent: 'space-between' }}>
                <div>
                  <Form
                    config={config}
                    data-testid="edit-basket-item-form"
                    disabled={formDisabled}
                    enableReinitialize
                    form={config.id}
                    handleOnChange={handleOnChange(id)}
                    initialValues={{ [ITEM_QUANTITY_INPUT_ID]: quantity && quantity.toString(), itemId: id }}
                    useFormValidationErrorsAlertMessage={false}
                  />
                  <BodyText>{config.unitText}</BodyText>
                </div>

                {shouldShowSaveForLater && <SaveForLaterButton />}
              </Container>
            </div>
          )}

          {!isItemFabric(item) && !showChildQuantityText && (
            <div>
              <BodyText marginBottom="1">Quantity</BodyText>
              <Container flex={shouldShowSaveForLater && { justifyContent: 'space-between' }}>
                <Stepper
                  className={styles.stepper}
                  data-testid="edit-basket-item-form"
                  disabled={formDisabled}
                  handleChange={handleStepperChange}
                  inputId={`${config.id}-quantity-stepper`}
                  inputName="quantity-selector"
                  max={maxQuantity}
                  onSubmit={onFormSubmit}
                  value={pendingQuantityUpdate || quantity}
                />

                {shouldShowSaveForLater && <SaveForLaterButton />}
              </Container>
            </div>
          )}

          {!isItemFabric(item) && showChildQuantityText && (
            <>
              {getQuantityText({ bold: true, quantity, styles })}
              {shouldShowSaveForLater && <SaveForLaterButton />}
            </>
          )}

          <Container block className={styles.itemSubtotalContainer} testId="edit-basket-item-subtotal-container">
            {isItemUpdating && <LoadingSpinner className={styles.spinner} />}
            {isChildItem && !isItemUpdating && (
              <>
                {getSubtotalText({
                  brand,
                  sidebar: false,
                  subtotal,
                  subtotalDiscounted,
                  styles,
                  basketView: true,
                  isChildItem,
                })}
              </>
            )}
          </Container>
          <div>{showError && <FormFieldErrorMessage id="edit-basket-error" message={errorMessage} roleAlert />}</div>
        </Container>
      </Container>

      {overlayType && (
        <EditBasketItemOverlay
          disabled={isProcessing}
          editBasketIsRemove={editBasketIsRemove}
          hasChildItems={hasChildItems}
          lastItemInBasket={lastItemInBasket}
          onRemoveNo={onRemoveItemNo}
          onRemoveYes={onRemoveItemYes}
          removeItemApiCallActive={removeItemApiCallActive}
          shouldShowSaveForLater={shouldShowSaveForLater}
          type={overlayType}
        />
      )}

      {hasChildItems && (
        <Container className={styles.childItemsContainer} testId="edit-basket-item-children">
          {children}
        </Container>
      )}
    </Container>
  );
};

export default EditBasketItem;
